import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Card,
  ColumnContainer,
  ElementContainer,
  FlexibleContainer,
  RowContainer,
} from "../../../components/containers/styledComponents";
import { usePost } from "../../../hooks/usePost";
import { useToast } from "../../../store/ToastProvider";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { LegendCircle } from "../../../components/visuals/styledComponents";
import { graphPaletteV2 } from "../../../styles";
import { generatePaletteColors } from "../../../utils/graphUtils";
import {
  ExposureResponseItem,
  FormattedExposureData,
} from "../../../models/stewardshipModels";
import {
  CommonDataItem,
  CommonDataRequestType,
  ExposureRequest,
} from "../../../types/CommonDataRequestTypes";
import { buildFundOptions, formatExposureData } from "../utils";
import { MESSAGES } from "../../../messages";
import { CustomDropdown } from "../../../components/visuals/PageFilter/PageFilter";
import { Filter } from "../../../types/PageFilterTypes";
import { ToggleOptionsButton } from "../../../components/buttons/ToggleOptionsButton";
import { CompanyExposureChart } from "./CompanyExposureChart";
import { CompanyExposureBars } from "./CompanyExposureBars";

const LegendContainer = styled.div<{ hasManyItems?: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${({ hasManyItems }): string => (hasManyItems ? "100%" : "330px")};
  max-height: 330px;
  width: 100%;
  justify-content: center;
  padding: 10px;
  padding-top: 20px;
  border-radius: 8px;
  overflow-y: auto;
`;

export const CompanyExposure = ({
  companyId,
}: CompanyExposureProps): JSX.Element => {
  const [selectedFunds, setSelectedFunds] = useState<Filter[]>([]);
  const [isLineChart, setIsLineChart] = useState(true);
  const { addToast } = useToast();
  const {
    isLoading,
    error: exposureRequestError,
    sendData: fetchExposureData,
    data: exposureData,
  } = usePost<ExposureResponseItem[], CommonDataItem<ExposureRequest>>();

  const DISPLAYED_CHART_OPTIONS = ["Weighted Exposure", "Absolute Exposure"];

  const formattedExposureData: FormattedExposureData[] = formatExposureData(
    exposureData || []
  );

  const filteredExposureData: FormattedExposureData[] =
    formattedExposureData.filter(
      (item) =>
        !selectedFunds.length ||
        selectedFunds.some((fund) => item.id === fund.key)
    );

  const handleOptionChange = (option: string): void => {
    setIsLineChart(option === DISPLAYED_CHART_OPTIONS[0]);
  };

  useEffect(() => {
    fetchExposureData("GET_COMMON_DATA", {
      name: CommonDataRequestType.EXPOSURE,
      item: { compass_cid: companyId },
    });
  }, [companyId]);

  useEffect(() => {
    if (exposureRequestError) {
      addToast(
        exposureRequestError.error_user_message ||
          MESSAGES.SCREENING_TOOL.ERROR_FETCHING_COMPANY_EXPOSURE,
        "error"
      );
    }
  }, [exposureRequestError]);

  const colors = generatePaletteColors(formattedExposureData, graphPaletteV2);
  const hasManyItems: boolean = formatExposureData.length > 10;
  return (
    <RowContainer ml={10} mt={1}>
      <Card w={100} h={"450px"} m="0 30px 0 10px" isLoading={isLoading}>
        <RowContainer mt={-1} jc="flex-start" ai="flex-start">
          <ColumnContainer ai="flex-start" w="75%">
            <CompassParagraph fontSize={16} bold={true}>
              Exposure
            </CompassParagraph>
            <RowContainer mt={8}>
              <CustomDropdown
                options={buildFundOptions(formattedExposureData)}
                placeholder={{ key: null, displayValue: "Select fund" }}
                isMultiSelect={true}
                onSelectOptions={setSelectedFunds}
                small={true}
                isLoading={isLoading}
              />
              <FlexibleContainer mr={"40px"}>
                <ToggleOptionsButton
                  options={DISPLAYED_CHART_OPTIONS}
                  handleOptionChange={handleOptionChange}
                />
              </FlexibleContainer>
            </RowContainer>
            {isLineChart ? (
              <CompanyExposureChart data={filteredExposureData} />
            ) : (
              <CompanyExposureBars data={filteredExposureData} />
            )}
          </ColumnContainer>
          <ColumnContainer w="25%" jc="center" mt={50}>
            <LegendContainer hasManyItems={hasManyItems}>
              {filteredExposureData.map((item, index) => (
                <ElementContainer key={index} mb={5}>
                  <LegendCircle color={colors[index]}></LegendCircle>
                  <CompassParagraph color="#8B909A" fontSize={12}>
                    {item?.id}
                  </CompassParagraph>
                </ElementContainer>
              ))}
            </LegendContainer>
          </ColumnContainer>
        </RowContainer>
      </Card>
    </RowContainer>
  );
};

type CompanyExposureProps = {
  companyId: string;
};
